import React, { memo } from "react";
import { Card } from "antd";
import { useParams } from "react-router-dom";
import ModuleTabContent from "components/layout-components/ModuleTabContent";
import ModuleSubTabs from "components/layout-components/ModuleSubTabs";
import { APP_PREFIX_PATH } from "configs/AppConfig";
import { EducationReportTab, EquipmentReportTab } from "./Reports";
import { EducationRegister, MemoRegister, ToolboxRegister, CompetencyRegister, EquipmentRegisterTab } from "./Registers";
import { useEducationStates, MonthlyEducationTab, ToolboxEducationTab, CompetencyTab, MemoContentTab, SopContentTab, MhraContentTab } from "./ManageEducation";
import { useManualTypes, ManualTypesTab, ManualsTab } from "./Manuals";
import SurveyTemplates from "./SurveyTemplates";
import SurveyFacilityAssignments from "./SurveyFacilityAssignments";
import SurveySubmission from "./SurveySubmission";
import SurveyHistory from "./SurveyHistory";
import Kpi from "./Kpi";
import KpiSetup from "./KpiSetup";
import ComplianceManual from "./ComplianceManual";

// "Employee Connect" in MyM's own nav (module_id 20, REACT_APP_URL_EDU) is
// really MajesticERP-EducationWeb, relabeled — confirmed by reading
// MenuContent.js's handleExternalClick directly. Its own nav (src/configs/
// NavigationConfig.js) has 6 top-level items; every screen from them is reused here, regrouped
// by subject (see Layout below):
//   Reports -> Reports.js (Education Register + Equipment tabs)
//   Registers -> Registers.js (Education, Equipment, Memo, Toolbox, Competency)
//   Manage Education -> ManageEducation.js (Monthly Education, Toolbox
//     Education, Memo, SOP, MHRA, Competency)
//   KPI -> Kpi.js
//   Survey -> Survey*.js (template-based surveys)
//   Manuals -> Manuals.js (Manual Types, Manuals)
// Its own Dashboard nav item is commented out/dead in the old app (a stub
// page, never routed) — not ported here either.
//
// The backend (MajesticERP-ToolboxAPI) is a shared "toolbox" API covering
// far more than education (equipment, KPIs, surveys, air purifiers, etc.) —
// only the education/employee-connect-relevant controllers were read in
// depth; Kpi/Survey are thin proxies straight through to MyM's own API, not
// local DB, so their real response shapes weren't independently confirmed.
//
// Known gaps, called out where they live in code too:
//  - Manuals: PDF file upload isn't wired (text/link field instead).
//  - KPI: report PDF is a text/link field, not an actual file upload.
//  - Toolbox Education (Manage Education): the real per-employee signature
//    capture flow isn't built — core fields only.
//  - Competency's assessment_details[] sub-form isn't built (sent empty).
//  - Registers' four facility-wise register endpoints all accept an optional
//    type-specific id filter (memo_id/competency_id/etc.) that isn't wired
//    up — these show the unfiltered "all" register for the chosen scope.
// Layout: header tabs are subjects (NavigationConfig "employee-connect"),
// and each subject has its tasks as in-page tabs with their own address
// (/app/employee-connect/<subject>/<task>). Every screen is the same
// component the old Reports / Registers / Manage Education / Manuals /
// Survey pages used; only where it lives changed.
const BASE = `${APP_PREFIX_PATH}/employee-connect`;

const Section = ({ items }) => {
  const { tab } = useParams();
  return (
    <Card bodyStyle={{ padding: "24px 28px" }}>
      <ModuleSubTabs basePath={`${BASE}/${tab}`} items={items} />
    </Card>
  );
};

const EducationSection = () => {
  const states = useEducationStates();
  return (
    <Section
      items={[
        { key: "monthly", label: "Monthly Education", render: () => <MonthlyEducationTab states={states} /> },
        { key: "register", label: "Register", render: () => <EducationRegister /> },
        { key: "report", label: "Report", render: () => <EducationReportTab /> },
      ]}
    />
  );
};

const ToolboxSection = () => {
  const states = useEducationStates();
  return (
    <Section
      items={[
        { key: "talks", label: "Toolbox Talks", render: () => <ToolboxEducationTab states={states} /> },
        { key: "register", label: "Register", render: () => <ToolboxRegister /> },
      ]}
    />
  );
};

const MemosSection = () => {
  const states = useEducationStates();
  return (
    <Section
      items={[
        { key: "memos", label: "Memos", render: () => <MemoContentTab states={states} /> },
        { key: "register", label: "Register", render: () => <MemoRegister /> },
      ]}
    />
  );
};

const CompetencySection = () => {
  const states = useEducationStates();
  return (
    <Section
      items={[
        { key: "competencies", label: "Competencies", render: () => <CompetencyTab states={states} /> },
        { key: "register", label: "Register", render: () => <CompetencyRegister /> },
      ]}
    />
  );
};

const EquipmentSection = () => {
  const { types, loading, reload } = useManualTypes();
  return (
    <Section
      items={[
        { key: "register", label: "Register", render: () => <EquipmentRegisterTab /> },
        { key: "report", label: "Report", render: () => <EquipmentReportTab /> },
        { key: "manuals", label: "Manuals", render: () => <ManualsTab types={types} /> },
        { key: "manual-types", label: "Manual Types", render: () => <ManualTypesTab types={types} loading={loading} reload={reload} /> },
      ]}
    />
  );
};

const PoliciesSection = () => {
  const states = useEducationStates();
  return (
    <Section
      items={[
        { key: "sop", label: "SOP", render: () => <SopContentTab states={states} /> },
        { key: "mhra", label: "MHRA", render: () => <MhraContentTab states={states} /> },
      ]}
    />
  );
};

// In the order they are used: build a survey, give it to facilities, fill
// it in, see the results. (Keys stay as they were, so links keep working.)
const SurveySection = () => (
  <Section
    items={[
      { key: "templates", label: "Survey Templates", render: () => <SurveyTemplates /> },
      { key: "assignments", label: "Assign Survey", render: () => <SurveyFacilityAssignments /> },
      { key: "submit", label: "Submit Survey", render: () => <SurveySubmission /> },
      { key: "history", label: "Survey Reports", render: () => <SurveyHistory /> },
    ]}
  />
);

// KPI: the same KPI rows and reports the app uses (ERP tables). Setup (first) holds a
// group's rows and each facility's frequency (weekly / monthly / quarterly);
// Reports fills one in and lists the PDFs.
const KpiSection = () => (
  <Section
    items={[
      { key: "setup", label: "KPI Setup", render: () => <KpiSetup /> },
      { key: "report", label: "KPI Reports", render: () => <Kpi /> },
    ]}
  />
);

const screens = {
  education: <EducationSection />,
  toolbox: <ToolboxSection />,
  memos: <MemosSection />,
  competency: <CompetencySection />,
  equipment: <EquipmentSection />,
  policies: <PoliciesSection />,
  kpi: <KpiSection />,
  "compliance-manual": <ComplianceManual />,
  survey: <SurveySection />,
};

const EmployeeConnect = () => <ModuleTabContent navKey="employee-connect" screens={screens} />;

export default memo(EmployeeConnect);
